import {
    type AbstractAuthenticationToken,
    AccountExpiredError,
    type AuthenticatedPrincipal,
    type Authentication,
    type AuthenticationManager,
    BadCredentialsError,
    CredentialsExpiredError,
    DisabledError,
    LockedError
} from './types.ts';
import { createDelegatingPasswordEncoder, type PasswordEncoder } from './crypto/index.ts';
import getLogger from '../../logger.ts';
import {
    NoopUserDetailsPasswordService,
    type UserDetails,
    type UserDetailsChecker,
    type UserDetailsPasswordService,
    type UserDetailsService,
    UsernameNotFoundError
} from './users/types.ts';

const logger = getLogger('security.users');

export function userDetailsServiceAuthenticationManager(
    userDetailsService: UserDetailsService,
    options?: {
        preAuthenticationChecks?: UserDetailsChecker;
        postAuthenticationChecks?: UserDetailsChecker;
        passwordEncoder?: PasswordEncoder;
        userDetailsPasswordService?: UserDetailsPasswordService;
    }
): AuthenticationManager {
    const preAuthenticationChecks = options?.preAuthenticationChecks ?? ((user: UserDetails) => {
        if (user.accountLocked) {
            logger.debug(`user account is locked`);
            throw new LockedError(`User account is locked`);
        }
        if (user.disabled) {
            logger.debug(`user account is disabled`);
            throw new DisabledError(`User is disabled`);
        }
        if (user.accountExpired) {
            logger.debug(`user account is expired`);
            throw new AccountExpiredError(`User account has expired`);
        }
    });
    const postAuthenticationChecks = options?.postAuthenticationChecks ?? ((user: UserDetails) => {
        if (user.credentialsExpired) {
            logger.debug(`user credentials have expired`);
            throw new CredentialsExpiredError(`User credentials have expired`);
        }
    });
    const passwordEncoder = options?.passwordEncoder ?? createDelegatingPasswordEncoder();

    const userDetailsPasswordService: UserDetailsPasswordService = options?.userDetailsPasswordService ?? NoopUserDetailsPasswordService;

    const upgradeEncodingIfNeeded = async (userDetails: UserDetails, presentedPassword?: string) => {
        const existingEncodedPassword = userDetails.password;
        const upgradeEncoding = existingEncodedPassword !== undefined
            && passwordEncoder.upgradeEncoding?.(existingEncodedPassword!);
        if (upgradeEncoding) {
            const newEncodedPassword = await passwordEncoder.encode(presentedPassword);
            return await userDetailsPasswordService.updatePassword(userDetails, newEncodedPassword);
        }
        return userDetails;
    }

    return async (authentication: Authentication): Promise<AbstractAuthenticationToken> => {

        const username = authentication.name!;
        const presentedPassword = (authentication.credentials !== undefined && authentication.credentials !== null) ? authentication.credentials.toString() : undefined;
        // retrieve user
        const user = await userDetailsService.findByUsername(username);

        if (!user) {
            throw new Error(`User not found: ${username}`);
        }

        preAuthenticationChecks(user);
        if (!(await passwordEncoder.matches(presentedPassword, user.password!))) {
            throw new BadCredentialsError('Invalid Credentials');
        }
        const principal = await upgradeEncodingIfNeeded(user, presentedPassword);

        postAuthenticationChecks(principal);
        let credentials = principal.password;
        return {
            type: 'UsernamePassword',
            principal: principal,
            credentials: credentials,
            authorities: principal.authorities,
            authenticated: true,
            name: principal.username,
            eraseCredentials() {
                credentials = null;
            }
        };
    };
}

export function accountStatusUserDetailsChecker(): UserDetailsChecker {
    return (user: UserDetails) => {
        if (user.accountLocked) {
            logger.debug(`failed to authenticate since user account is locked`);
            throw new LockedError(`User account is locked`);
        }
        if (user.disabled) {
            logger.debug(`failed to authenticate user account is disabled`);
            throw new DisabledError(`User is disabled`);
        }
        if (user.accountExpired) {
            logger.debug(`failed to authenticate since user account is expired`);
            throw new AccountExpiredError(`User account has expired`);
        }
        if (user.credentialsExpired) {
            logger.debug(`failed to authenticate since user credentials have expired`);
            throw new CredentialsExpiredError(`User credentials have expired`);
        }
    }
}

export function preAuthenticatedAuthenticationManager(opts: {
    userDetailsService: UserDetailsService,
    userDetailsChecker?: UserDetailsChecker
}): AuthenticationManager {
    const userDetailsService = opts.userDetailsService;
    const userDetailsChecker = opts.userDetailsChecker ?? accountStatusUserDetailsChecker();

    const supports = (authentication: Authentication): authentication is AbstractAuthenticationToken & AuthenticatedPrincipal => {
        return (authentication.type === 'PreAuthenticated' && authentication.name !== undefined);
    }

    return async (authentication: Authentication): Promise<AbstractAuthenticationToken> => {
        const userDetails = supports(authentication) && await userDetailsService.findByUsername(authentication.name);
        if (!userDetails) {
            throw new UsernameNotFoundError(`user not found`, authentication.name);
        }
        userDetailsChecker(userDetails);
        let erasableCredentials = authentication.credentials;
        const result: AbstractAuthenticationToken = {
            type: 'PreAuthenticated',
            principal: userDetails,
            credentials: erasableCredentials,
            authorities: userDetails.authorities,
            authenticated: true,
            details: userDetails,
            name: userDetails.username,
            eraseCredentials() {
                erasableCredentials = null;
            }
        };
        return result;
    };
}
